import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Switch,
  Paper,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Typography,
  Tooltip,
} from '@mui/material';
import { ImportExport, MoreHoriz } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useQueryParams, StringParam } from 'use-query-params';
import { get } from 'lodash';
import EditIcon from '../../../assets/icons/edit-icon.svg';
import {
  ControlLocationType,
  selectFilterLocationState,
  selectLocationState,
  setControlFilterLocationsLimit,
} from '../../../state/modules/control/controlReducer';
import Pagination from '../../../common/pagination/Pagination';
import { useGetAllAgenciesQuery } from '../../../services/agencies.service';
import { useAuth } from '../../../hooks/useAuth';
import { Grid, Table, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import {
  CustomFieldType,
  getTableCell,
  TableHeaderCell,
  TableHeaderContent,
} from '../../../common/DxTable/DxTableCommon';
import { ImageIcon } from '../../../utils/UtilsComponent';
import { CursorType } from '../../../configs/constant';
import { useAppDispatch } from '../../../state/store';
import { useLazyGetControlLocationsFilterQuery } from '../../../services/control.service';
import { BASE_URL } from 'services/http.service';
import { useGetProvincesQuery } from 'services/geography.service';
import { Table as TableFilter } from '@devexpress/dx-react-grid-material-ui';
import * as XLSX from 'xlsx';

export interface FiltersFormValue {
  status: string;
  agencyId: string;
  province: string;
  alert_mode: string;
}

const ActionCellContent = ({
  cellProps,
  onActionClick,
}: {
  cellProps: Table.DataCellProps;
  onActionClick: (type: string, id: string | any) => void;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const row = useMemo(() => cellProps.row, [cellProps]);

  return (
    <div>
      <IconButton
        id="demo-positioned-button"
        aria-controls={open ? 'demo-positioned-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreHoriz />
      </IconButton>
      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => onActionClick('detail', row)} sx={{ padding: '16px' }}>
          <ListItemIcon>
            <ImageIcon image={EditIcon} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { fontSize: '14px' } }}>Chi tiết</ListItemText>
        </MenuItem>
        <Divider sx={{ margin: '0 16px !important' }} />
      </Menu>
    </div>
  );
};

const locationStatus = [
  { value: 'connected', label: 'Hoạt động' },
  { value: 'alert', label: 'Cảnh báo' },
  { value: 'warning', label: 'Cảm biến' },
  { value: 'disconnected', label: 'Mất kết nối' },
];
const gatewayAlertMode = [
  { value: 'on', label: 'Bật báo động' },
  { value: 'off', label: 'Tắt báo động' },
];

const getDiffDay = (end: string) => {
  const now = dayjs(new Date());
  const endDate = dayjs(new Date(end));
  if (now.isAfter(endDate)) {
    return 'Hết hạn';
  }
  return endDate.diff(now, 'days');
};

export function getMinutesDiffNow(timestamp: number) {
  const now = dayjs(Date.now());
  const nodeStateTime = dayjs(new Date(timestamp * 1000));
  return now.diff(nodeStateTime, 'minutes');
}

export default function Filters({
  handleOpenLocation,
  filtersFormValue,
  setFiltersFormValue,
  handleClearFilter,
  handleFilter,
}: {
  handleOpenLocation: (localtion: ControlLocationType) => void;
  filtersFormValue: FiltersFormValue;
  setFiltersFormValue: (filtersFormValue: FiltersFormValue) => void;
  handleClearFilter: () => void;
  handleFilter: () => void;
}) {
  const [paginate, setPaginate] = useState<CursorType>({});
  const { filterLocationsCursor, filterLocationslimit, filterLocationsTotal } = useSelector(selectLocationState);
  const [query, setQuery] = useQueryParams({
    locationId: StringParam,
    agencyId: StringParam,
    status: StringParam,
    province: StringParam,
    alert_mode: StringParam,
  });
  const {
    auth: { currentUser, currentAgency },
  } = useAuth();
  const locations = useSelector(selectFilterLocationState) as ControlLocationType[];
  const dispatch = useAppDispatch();

  const { data: allAgenciesData } = useGetAllAgenciesQuery({ id: currentUser?.sub_id || '' });
  const agencies = (allAgenciesData?.data || []).concat(currentAgency ? [currentAgency] : []);
  const [getControlFilterLocationsQuery] = useLazyGetControlLocationsFilterQuery();
  const { data: provincesData = [] } = useGetProvincesQuery();

  const provinceOptions = provincesData.map((item: any) => ({
    name: item.fullName || item.name,
    id: item.slug,
  }));

  const columns = [
    { name: 'name', title: 'Tên vị trí' },
    { name: 'address', title: 'Địa chỉ' },
    { name: 'business', title: 'Loại hình KD' },
    { name: 'agency_name', title: 'Đại lý' },
    { name: 'contact_name', title: 'Người liên hệ' },
    { name: 'contact_number', title: 'SĐT người liên hệ' },
    { name: 'maintaint_date', title: 'Ngày bảo trì' },
    { name: 'tags', title: 'Thẻ tag' },
    { name: 'serial', title: 'Serial' },
    { name: 'connection', title: 'Trạng thái' },
    { name: 'alert', title: 'Chế độ cảnh báo' },
    { name: 'action', title: 'Hành động' },
  ];

  const tableColumnExtensions = [
    { columnName: 'name', width: 350 },
    { columnName: 'serial', width: 200 },
    { columnName: 'alert', width: 150 },
    { columnName: 'action', width: 150, align: 'center' },
  ] as Table.ColumnExtension[];

  const handleSetLimit = (limit: number) => {
    dispatch(setControlFilterLocationsLimit({ limit }));
  };

  useEffect(() => {
    if (currentUser) {
      getControlFilterLocationsQuery({
        agency_id: currentUser?.sub_id,
        params: {
          state: filtersFormValue.status !== 'all' ? filtersFormValue.status : undefined,
          province: filtersFormValue.province !== 'all' ? filtersFormValue.province : undefined,
          alert_mode: filtersFormValue.alert_mode !== 'all' ? filtersFormValue.alert_mode : undefined,
          limit: filterLocationslimit,
           ...paginate
        },
      });
    }
  }, [paginate, currentUser, filterLocationslimit]);

  const handleChange = (e: any, filterName: 'status' | 'agencyId' | 'province' | 'alert_mode') => {
    const value = e.target.value;
    setFiltersFormValue({
      ...filtersFormValue,
      [filterName]: value,
    } as FiltersFormValue);
  };

  const handleClick = (type: string, row: any) => {
    if (type === 'detail') {
      setQuery({ ...query, locationId: row.id });
    }
  };

  const ClickableRow = ({ row, ...restProps }: any) => (
    <TableFilter.Row
      {...restProps}
      onClick={() => handleClick('detail', row)}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
      }}
    />
  );

  const handleExport = async () => {
    const token = localStorage.getItem('access_token');
    if (!currentUser || !token) return;
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${JSON.parse(token)}`);
    const queryParams = new URLSearchParams();

    if (currentUser.type !== 'organization') {
      queryParams.append('type', 'csv');
    }
    if (query.agencyId && query.agencyId !== 'all') {
      queryParams.append('agency_id', query.agencyId);
    }
    if (query.status && query.status !== 'all') {
      queryParams.append('state', query.status);
    }
    if (query.province && query.province !== 'all') {
      queryParams.append('province', query.province);
    }
    if (query.alert_mode && query.alert_mode !== 'all') {
      queryParams.append('alert_mode', query.alert_mode);
    }

    const exportPath = currentUser.type === 'organization' ? 'locations/export' : `agencies/${currentUser.sub_id}/locations/export`;
    const URL_CONTENT = queryParams.toString() ? `${exportPath}?${queryParams.toString()}` : exportPath;

    try {
      const response = await fetch(`${BASE_URL}/${URL_CONTENT}`, {
        method: 'GET',
        headers: myHeaders,
      });

      if (!response.ok) throw new Error('Failed to fetch the CSV file');
      const csvText = await response.text();
      const workbook = XLSX.read(csvText, { type: 'string' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      if (worksheet['!ref']) {
        const range = XLSX.utils.decode_range(worksheet['!ref']);

        for (let row = range.s.r + 1; row <= range.e.r; row += 1) {
          const cellAddress = `G${row + 1}`;
          const cell = worksheet[cellAddress];

          if (!cell || cell.v === undefined || cell.v === null || cell.v === '') continue;

          const serialValue = Number(cell.v);
          if (Number.isNaN(serialValue)) continue;

          cell.t = 'n';
          cell.v = serialValue;
          cell.z = 'dd/mm/yyyy';
        }
      }

      XLSX.writeFile(workbook, 'locationData.xlsx');
    } catch (error) {
      console.log(error);
    }
  };

  const handleClearAllFilter = () => {
    handleClearFilter();
    setPaginate({});
  };

  const customField = {
    tags: {
      renderContent: ({ row }) => {
        if (!row?.tags) return '--';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              sx={{
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                fontSize: '14px',
                color: '#1AA6EE',
              }}
            >
              {row?.tags.join(', ')}
            </Typography>
          </Box>
        );
      },
    },
    maintaint_date: {
      renderContent: ({ row }) => {
        return (
          <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>
            {row.maintaint_date ? dayjs(row.maintaint_date)?.format('DD/MM/YYYY') : '--'}
          </Typography>
        );
      },
    },
    serial: {
      renderContent: ({ row }) => {
        return <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>{row.gateway?.serial || '--'}</Typography>;
      },
    },
    alert: {
      renderContent: ({ row }) => {
        return (
          <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>
            {row.gateway?.state?.mode === 1 ? 'Bật' : 'Tắt'}
          </Typography>
        );
      },
    },
    address: {
      renderContent: ({ row }) => {
        return (
          <Tooltip title={row.address}>
            <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>{row.address || '--'}</Typography>
          </Tooltip>
        );
      },
    },
    name: {
      renderContent: ({ row }) => {
        return (
          <Tooltip title={row.name}>
            <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>{row.name || '--'}</Typography>
          </Tooltip>
        );
      },
    },
    connection: {
      renderContent: ({ row }) => {
        return (
          <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>
            {row.gateway?.state?.connection === 'online' ? 'Kết nối' : 'Mất kết nối'}
          </Typography>
        );
      },
    },
  } as CustomFieldType;

  return (
    <Box
      style={{
        padding: '0 24px',
        marginBottom: '100px',
      }}
    >
      <Box mt="24px">
        <Box mb="20px" display="flex" justifyContent="space-between">
          <Box>
            <FormControl variant="outlined" size="small" style={{ width: '200px', marginRight: '16px' }}>
              <InputLabel htmlFor="province" style={{ backgroundColor: '#ffffff' }}>
                Tỉnh thành
              </InputLabel>
              <Select
                value={filtersFormValue.province}
                onChange={(e) => handleChange(e, 'province')}
                inputProps={{
                  style: {
                    fontSize: '14px',
                  },
                }}
              >
                <MenuItem value={'all'}>Tất cả tỉnh thành</MenuItem>
                {provinceOptions.map((item: any) => (
                  <MenuItem key={item.id} value={item.id} style={{ fontSize: '14px' }}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ width: '200px', marginRight: '16px' }}>
              <InputLabel htmlFor="agency" style={{ backgroundColor: '#ffffff' }}>
                Đại lý
              </InputLabel>
              <Select
                value={filtersFormValue.agencyId}
                onChange={(e) => handleChange(e, 'agencyId')}
                inputProps={{
                  style: {
                    fontSize: '14px',
                  },
                }}
              >
                <MenuItem value={'all'}>Tất cả đại lý</MenuItem>
                {agencies.map((item: any) => (
                  <MenuItem key={item.id} value={item.id} style={{ fontSize: '14px' }}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl style={{ width: '200px', marginRight: '16px' }} variant="outlined" size="small">
              <InputLabel id="status" style={{ backgroundColor: '#ffffff' }}>
                Trạng Thái Vị Trí
              </InputLabel>
              <Select
                labelId="status"
                id="demo-simple-select"
                value={filtersFormValue.status}
                onChange={(e) => handleChange(e, 'status')}
                inputProps={{
                  style: {
                    fontSize: '14px',
                  },
                }}
              >
                <MenuItem value={'all'}>Tất cả trạng thái</MenuItem>
                {locationStatus.map((item) => (
                  <MenuItem key={item.value} value={item.value} style={{ fontSize: '14px' }}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl style={{ width: '200px', marginRight: '16px' }} variant="outlined" size="small">
              <InputLabel id="status" style={{ backgroundColor: '#ffffff' }}>
                Trạng Thái Gateway
              </InputLabel>
              <Select
                labelId="status"
                id="demo-simple-select"
                value={filtersFormValue.alert_mode}
                onChange={(e) => handleChange(e, 'alert_mode')}
                inputProps={{
                  style: {
                    fontSize: '14px',
                  },
                }}
              >
                <MenuItem value={'all'}>Tất cả trạng thái</MenuItem>
                {gatewayAlertMode.map((item) => (
                  <MenuItem key={item.value} value={item.value} style={{ fontSize: '14px' }}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              onClick={handleFilter}
              color="primary"
              variant="contained"
              style={{
                height: '40px',
                padding: '0 16px',
                marginRight: '16px',
              }}
            >
              Lọc
            </Button>
            <Button
              onClick={handleClearAllFilter}
              color="primary"
              variant="outlined"
              style={{ height: '40px', padding: '0 16px' }}
            >
              Xoá bộ lọc
            </Button>
          </Box>
          <Box>
            <Button
              onClick={handleExport}
              color="primary"
              variant="contained"
              style={{ height: '40px', padding: '0 16px' }}
              startIcon={<ImportExport />}
            >
              Xuất excel
            </Button>
          </Box>
        </Box>
      </Box>
      <Box>
        <Paper sx={{ boxShadow: 'none', position: 'relative', maxHeight: 332, overflow: 'auto' }}>
          <Grid rows={locations} columns={columns}>
            <Table
              columnExtensions={tableColumnExtensions}
              rowComponent={ClickableRow}
              cellComponent={(props) =>
                getTableCell(props, <ActionCellContent cellProps={props} onActionClick={handleClick} />, customField)
              }
            />
            <TableHeaderRow cellComponent={TableHeaderCell} contentComponent={TableHeaderContent} />
          </Grid>
        </Paper>
        <Pagination
          paginate={filterLocationsCursor}
          setPaginate={setPaginate}
          limit={filterLocationslimit}
          setLimit={handleSetLimit}
          total={filterLocationsTotal}
        />
      </Box>
    </Box>
  );
}
